import assign from 'lodash/assign';
import fromPairs from 'lodash/fromPairs';
import isEqual from 'lodash/isEqual';
import merge from 'lodash/merge';
import values from 'lodash/values';

import { getConfigValues } from 'src/config';
import Routes from 'src/config/routes';

// Takes two arrays of objects that share a common property, and merges
// objects where that property matches into a new array
// Note: This is will only return matched objects. Anything without a
//       matching id in both arrays will be discarded
export const mergeById = <T1, T2>(
  arr1: readonly T1[],
  arr2: readonly T2[],
  id: Extract<keyof T1, keyof T2>,
): Array<T1 & T2> => {
  const transform = (arr: ReadonlyArray<T1 | T2>) =>
    fromPairs(arr.map((item) => [item[id], item]));
  const filter = (res: any[]) =>
    res.filter((obj) => !arr1.some((x) => isEqual(x, obj)));
  return filter(values(merge({}, transform(arr1), transform(arr2))));
};

export const replaceById = <T>(arr: T[], item: T, id: keyof T): T[] =>
  arr.map((i) => (i[id] === item[id] ? item : i));

export const applyPartial = <T>(base: T, partial: Partial<T>): T =>
  assign({}, base, partial);

// eslint-disable-next-line
export const noop = (...args: any[]): any => {};

export function assertIsDefined<T>(
  val: T,
  msg?: string,
): asserts val is NonNullable<T> {
  if (val === undefined || val === null) {
    throw new Error(msg ?? `Expected 'val' to be defined, but received ${val}`);
  }
}

// required for forms-ui types
export const withoutHighlight = <T extends { highlightRanges?: any }>(
  obj: T,
) => {
  const { highlightRanges, ...rest } = obj;
  return rest;
};

export const getRange = (start: number = 0, end: number) =>
  Array.from({ length: end - start }, (v, k) => k + start);

export const shuffledList = <T>(array: T[]) =>
  array
    .map((element) => ({ element, r: Math.random() }))
    .sort((a, b) => a.r - b.r)
    .map((a) => a.element);

// https://www.typescriptlang.org/docs/handbook/unions-and-intersections.html#union-exhaustiveness-checking
export const assertNever = (x: never): never => {
  throw new Error(`Unexpected object: ${x}`);
};

export const isNonNullable = <T>(x: T): x is NonNullable<T> =>
  x !== null && x !== undefined;

export const wait = (timeMs: number) =>
  new Promise((resolve) => setTimeout(() => resolve(null), timeMs));

export enum ConsoleErrorId {
  ExpireListingValidation = 'ExpireListingValidation',
  PostListingValidation = 'PostListingValidation',
  StripeUninitialised = 'StripeUninitialised',
  PaymentProductValidation = 'PaymentProductValidation',
  PaymentFailedToFindProduct = 'PaymentFailedToFindProduct',
  PaymentEmailBoostMissingListing = 'PaymentEmailBoostMissingListing',
  SignupNewsletterRequestParseFailure = 'SignupNewsletterRequestParseFailure',
  SignupNewsletterRequestUnknownFailure = 'SignupNewsletterRequestUnknownFailure',
  SignupNewsletterRequestServerValidation = 'SignupNewsletterRequestServerValidation',
  SignupNewsletterNetworkRequest = 'SignupNewsletterNetworkRequest',
}

// Wrap console.error so we can stub it in our tests. This allows us to differentiate
// between deliberate errors and accidental errors
export const consoleErrorWrapper = (id: ConsoleErrorId, ...args: any[]) =>
  // eslint-disable-next-line no-console
  console.error(id, ...args);

export const createDotNetLinkUrl = (path: string) =>
  `${getConfigValues().HEADER_SERVER_BASE}${path}`;

export const createEditListingUrl = (clientId: string, listingId: string) => {
  const searchParams = new URLSearchParams();
  searchParams.set('ClientId', clientId);
  searchParams.set('listingId', listingId);

  return `${getConfigValues().ADVERTISER_CENTRE_URL}${Routes.CREATE_AD_URL}?${searchParams.toString()}`;
};

export const createPostListingUrl = (clientId: number, listingId: number) => {
  const searchParams = new URLSearchParams();
  searchParams.set('ClientId', clientId.toString());
  searchParams.set('listingId', listingId.toString());
  searchParams.set('p', '7');
  searchParams.set('Preview', true.toString());

  return `${getConfigValues().ADVERTISER_CENTRE_URL}${Routes.CREATE_AD_URL}?${searchParams.toString()}`;
};

export const createPreviewListingUrl = (
  clientId: number,
  listingId: number,
) => {
  const searchParams = new URLSearchParams();
  searchParams.set('ClientId', clientId.toString());
  searchParams.set('FromList', true.toString());
  searchParams.set('Preview', true.toString());
  searchParams.set('listingId', listingId.toString());

  return `${getConfigValues().ADVERTISER_CENTRE_URL}${Routes.CREATE_AD_URL}?${searchParams.toString()}`;
};
